import { calculateRideMetrics, type RideMetrics, type RidePoint, type RideProgress } from "./ride";

type Position = Pick<RideProgress, "currentPoint" | "pointIndex"> & Partial<Pick<RideProgress, 'fraction'>>;

/** Compiles cumulative telemetry once, then evaluates only the current partial segment. */
export function compileRideMetrics(points: readonly RidePoint[], timerDurationSeconds?: number): (position: Position) => RideMetrics {
  // Use the shared calculator for validation and each segment's metric semantics.
  calculateRideMetrics(points, timerDurationSeconds);
  const first = points[0]!;
  const startTime = Date.parse(first.recordedAt);
  const elapsedDuration = (Date.parse(points.at(-1)!.recordedAt) - startTime) / 1_000;
  const elapsed = (point: RidePoint) => (Date.parse(point.recordedAt) - startTime) / 1_000;
  const totals: RideMetrics[] = [calculateRideMetrics([first, first])];
  for (let index = 1; index < points.length; index += 1) {
    totals.push(combine(totals[index - 1]!, calculateRideMetrics([points[index - 1]!, points[index]!]), elapsed(points[index]!)));
  }
  // FIT files without timer events supply only a total; interpolate the display clock.
  const durationAt = (position: Position) => timerDurationSeconds === undefined ? elapsed(position.currentPoint)
    : elapsedDuration === 0 ? timerDurationSeconds * (position.fraction ?? position.pointIndex / (points.length - 1))
    : timerDurationSeconds * (elapsed(position.currentPoint) / elapsedDuration);
  return (position) => combine(
    totals[position.pointIndex]!,
    calculateRideMetrics([points[position.pointIndex]!, position.currentPoint]),
    durationAt(position),
  );
}

/** Joins adjacent metric spans, deriving average speed from their combined time. */
function combine(prefix: RideMetrics, segment: RideMetrics, durationSeconds: number): RideMetrics {
  const distanceKilometers = prefix.distanceKilometers + segment.distanceKilometers;
  return {
    distanceKilometers, durationSeconds,
    ascentMeters: prefix.ascentMeters + segment.ascentMeters,
    descentMeters: prefix.descentMeters + segment.descentMeters,
    minimumElevationMeters: Math.min(prefix.minimumElevationMeters, segment.minimumElevationMeters),
    maximumElevationMeters: Math.max(prefix.maximumElevationMeters, segment.maximumElevationMeters),
    averageKilometersPerHour: durationSeconds === 0 ? 0 : distanceKilometers / (durationSeconds / 3_600),
  };
}
